import {
  comboboxActions,
  findNode,
  getEditorString,
  getPointAfter,
  insertText,
  select,
  setNodes,
  type TNodeEntry,
} from '@udecode/plate'
import { ApolloError } from '@apollo/client'
import type {
  BufferEditor,
  BufferValue,
} from '~publish/legacy/editor/BufferEditor/types.plate'
import type { AutocompleteItem } from '../../autocomplete'
import { LinkedInAnnotationInputElement } from '../nodes'
import { client, gql } from '~publish/legacy/apollo-client/index'

import type { ServiceMention } from '~publish/gql/graphql'
import {
  LINKEDIN_ENTITY_URL_REGEX,
  LINKEDIN_PROFILE_URL_REGEX,
  LinkedInAnnotation,
} from '../LinkedInAnnotations'
import { getRecentItems } from '../queries/recentSuggestions'
import { selectedChannelsForTracking } from '~publish/legacy/composer/composer/editor/ComposerEditor/hooks/useSelectedChannelsForTracking'

const GetServiceMentions = gql(`
  query GetServiceMentions($channelIds: [ChannelId!]!, $query: String!) {
    serviceMentions(input: { channelIds: $channelIds, query: $query }) {
      displayName
      link
      id
      entity
      vanityName
      image
    }
  }
`)

export function parseSuggestions(
  suggestions: ServiceMention[],
): AutocompleteItem[] {
  return suggestions.map((suggestion) => ({
    key: suggestion.entity,
    text: suggestion.displayName,
    data: {
      id: suggestion.id,
      entity: suggestion.entity,
      name: suggestion.displayName,
      vanityName: suggestion.vanityName,
      localizedName: suggestion.displayName,
      avatar: suggestion.image,
      link: suggestion.link,
    },
  }))
}

function setIsLoading(
  editor: BufferEditor,
  mentionEntry: TNodeEntry<LinkedInAnnotationInputElement>,
  isLoading: boolean,
): void {
  setNodes(
    editor,
    {
      isLoading,
    },
    {
      at: mentionEntry[1],
    },
  )
}

/**
 * Callback executed to fetch new Linkedin mention suggestions.
 *
 * Updates combobox items on success.
 */
export async function getNewSuggestionsOnMentionUpdate(
  editor: BufferEditor,
): Promise<void> {
  const mentionEntry = findNode<LinkedInAnnotationInputElement, BufferValue>(
    editor,
    { match: (n) => LinkedInAnnotationInputElement.is(n) },
  )

  const editorComposerData = editor.getEditorComposerData?.(editor)
  const channelIds = editorComposerData?.selectedProfilesForService.map(
    (profile) => profile.id,
  )

  if (mentionEntry && channelIds?.length) {
    let query = getEditorString(editor, mentionEntry[1]).replace(
      LinkedInAnnotationInputElement.trigger,
      '',
    )

    if (query.length === 0) {
      const recentItems = getRecentItems()

      if (recentItems) {
        comboboxActions.items(recentItems)
      }
    }

    if (query.length < 3) {
      return
    }

    setIsLoading(editor, mentionEntry, true)

    const trackingDetails = selectedChannelsForTracking({
      serviceName: 'linkedin',
    })

    // Handle cases where the user pastes a URL
    if (query.match(/^https?:\/\//)) {
      if (query.includes('https://www.linkedin.com/in/')) {
        // Check if the pasted URL is a LinkedIn profile URL and use the vanity name to find a user

        query =
          query.match(LINKEDIN_PROFILE_URL_REGEX)?.groups?.vanityName || ''
      } else {
        if (!query.match(LINKEDIN_ENTITY_URL_REGEX)) {
          // If the pasted URL is not a LinkedIn profile URL, do not proceed

          comboboxActions.items([
            {
              key: 'not-found',
              text: '',
            },
          ])

          trackingDetails.sendMentionURLPastedFailed(query)

          setIsLoading(editor, mentionEntry, false)

          return
        } else {
          // If the pasted URL is not a profile URL, fetch the entity details using the URL
          const entityDetails =
            await LinkedInAnnotation.getLinkedInEntityDetails(
              query,
              channelIds[0],
            )

          setIsLoading(editor, mentionEntry, false)

          if (!entityDetails) {
            comboboxActions.items([
              {
                key: 'not-found',
                text: '',
              },
            ])

            trackingDetails.sendMentionURLPastedFailed(query)

            return
          }

          setNodes<LinkedInAnnotationInputElement>(
            editor,
            LinkedInAnnotationInputElement.new('', entityDetails),
            {
              at: mentionEntry[1],
            },
          )

          trackingDetails.sendMentionURLPastedSucceeded(query)

          insertText(editor, entityDetails.localizedName, {
            at: [...mentionEntry[1], 0],
          })

          const pointAfter = getPointAfter(editor, mentionEntry[1])

          if (!pointAfter) {
            return
          }
          // Insert a space after the mention
          insertText(editor, ' ', {
            at: pointAfter,
          })

          // Select the point after the mention
          const endOfMention = getPointAfter(editor, pointAfter)

          if (!endOfMention) {
            return
          }

          select(editor, endOfMention)

          return
        }
      }
    }

    try {
      const { data } = await client.query({
        query: GetServiceMentions,
        variables: {
          channelIds,
          query,
        },
      })

      if (data?.serviceMentions) {
        if (data.serviceMentions.length === 0) {
          comboboxActions.items([
            {
              key: 'empty',
              text: '',
            },
          ])
        } else {
          comboboxActions.items(parseSuggestions(data.serviceMentions))
        }
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        if (error.message.includes('status code 403')) {
          setNodes(
            editor,
            {
              isPermissionError: true,
              channelId:
                error.graphQLErrors[0].extensions.channelId || channelIds[0],
            },
            {
              at: mentionEntry[1],
            },
          )
        } else {
          comboboxActions.items([
            {
              key: 'empty',
              text: '',
            },
          ])
        }
      }
    } finally {
      setIsLoading(editor, mentionEntry, false)
    }
  }
}
